import React, { useMemo, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';

const DEFAULT_CONFIG = { fields: { phone: 'required', message: 'optional' }, checks: [], tab: 'registration' };

const normalizeForm = (config, key) => ({
  ...config,
  key: config.key || key,
  fields: config.fields && typeof config.fields === 'object' ? config.fields : DEFAULT_CONFIG.fields,
  checks: Array.isArray(config.checks) ? config.checks : [],
  tab: config.tab || 'registration'
});

export const registrationRequestForms = article => {
  try {
    const config = typeof article.registration_request_config === 'string' ? JSON.parse(article.registration_request_config) : article.registration_request_config;
    if (!config || typeof config !== 'object') return [{ ...DEFAULT_CONFIG, key: 'default' }];
    if (Array.isArray(config.forms)) {
      const forms = config.forms.filter(form => form && typeof form === 'object').map((form, index) => normalizeForm(form, `form-${index + 1}`));
      return forms.length ? forms : [{ ...DEFAULT_CONFIG, key: 'default' }];
    }
    return [normalizeForm(config, 'default')];
  } catch {
    return [{ ...DEFAULT_CONFIG, key: 'default' }];
  }
};

export const registrationRequestConfig = article => registrationRequestForms(article)[0];

const ConfirmationText = ({ text }) => {
  const blocks = [];
  String(text).split(/\r?\n/).map(line => line.trim()).filter(Boolean).forEach(line => {
    const item = line.match(/^[-•]\s+(.*)$/);
    if (item) {
      const last = blocks[blocks.length - 1];
      if (last?.list) last.items.push(item[1]); else blocks.push({ list: true, items: [item[1]] });
    } else blocks.push({ text: line });
  });
  return blocks.map((block, index) => block.list
    ? <ul key={index}>{block.items.map((item, itemIndex) => <li key={itemIndex}>{item}</li>)}</ul>
    : <p key={index}>{block.text}</p>);
};

const RegistrationRequestForm = ({ article, form, preview = false }) => {
  const { user } = useAuth();
  const config = useMemo(() => form || registrationRequestConfig(article), [article, form]);
  const fields = config.fields;
  const [values, setValues] = useState({ phone: user?.phone || '', address_line: '', city: '', state: '', postal_code: '', birthdate: '', message: '' });
  const [checked, setChecked] = useState(() => config.checks.map(() => false));
  const [certificate, setCertificate] = useState(null);
  const [choice, setChoice] = useState('');
  const [saving, setSaving] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const heading = config.heading || `Request a ${article.title} account`;
  const submitLabel = config.submit_label || heading;
  const set = key => event => setValues(current => ({ ...current, [key]: event.target.value }));
  const required = key => fields[key] === 'required';
  const showAddress = 'address' in fields;
  const showCityState = !showAddress && ('city' in fields || 'state' in fields);

  const submit = async event => {
    event.preventDefault();
    setError('');
    setSaving(true);
    const body = new FormData();
    body.append('request_type', 'registration');
    body.append('resource_slug', article.slug);
    if (config.key) body.append('form_key', config.key);
    if (config.choice) body.append('choice', choice);
    body.append('message', values.message);
    if ('phone' in fields) body.append('phone', values.phone);
    if (showAddress) ['address_line', 'city', 'state', 'postal_code'].forEach(key => body.append(key, values[key]));
    if (showCityState) ['city', 'state'].forEach(key => body.append(key, values[key]));
    if ('birthdate' in fields) body.append('birthdate', values.birthdate);
    if (config.checks.length) body.append('checks', JSON.stringify(checked.flatMap((value, index) => (value ? [index] : []))));
    if ('certificate' in fields && certificate) body.append('certificate', certificate);
    try {
      await api.post('/vax-account-requests', body, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSubmitted(true);
    } catch (failure) {
      const shown = {
        checks: config.checks.length > 0, choice: Boolean(config.choice), certificate: 'certificate' in fields, phone: 'phone' in fields, birthdate: 'birthdate' in fields,
        address_line: showAddress, postal_code: showAddress, city: showAddress || 'city' in fields, state: showAddress || 'state' in fields
      };
      const outdated = failure.response?.status === 400 && (failure.response.data?.fields || []).some(name => name in shown && !shown[name]);
      setError(outdated
        ? 'This form has been updated since you opened the page. Please refresh the page (Ctrl+Shift+R, or Cmd+Shift+R on a Mac) and submit it again.'
        : failure.response?.data?.error || 'Unable to submit your request. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (preview) return <div className="ar-vax-request"><strong>{heading}</strong><p>The request form will be available to agents after this page is published.</p></div>;
  if (submitted) return (
    <div className="ar-vax-request" role="status">
      <strong>Request submitted</strong>
      {config.confirmation ? <ConfirmationText text={config.confirmation} /> : <p>Therese will review your request. You will receive an email when it is approved.</p>}
    </div>
  );

  return (
    <form className="ar-vax-request" onSubmit={submit}>
      <h3>{heading}</h3>
      <p>Submitted as {user?.name || 'your agent account'} ({user?.email}). Do not enter any passwords.</p>
      {'phone' in fields && <label>Phone number <input type="tel" required={required('phone')} maxLength={50} value={values.phone} onChange={set('phone')} /></label>}
      {showAddress && <label>Street address <input type="text" required={required('address')} maxLength={255} value={values.address_line} onChange={set('address_line')} /></label>}
      {(showAddress || showCityState) && <>
        <label>City <input type="text" required={showAddress ? required('address') : required('city')} maxLength={100} value={values.city} onChange={set('city')} /></label>
        <label>State <input type="text" required={showAddress ? required('address') : required('state')} maxLength={100} value={values.state} onChange={set('state')} /></label>
      </>}
      {showAddress && <label>ZIP code <input type="text" required={required('address')} maxLength={20} value={values.postal_code} onChange={set('postal_code')} /></label>}
      {'birthdate' in fields && <label>Date of birth <input type="date" required={required('birthdate')} min="1900-01-01" max={new Date().toISOString().slice(0, 10)} value={values.birthdate} onChange={set('birthdate')} /></label>}
      {config.choice && (
        <fieldset className="ar-vax-choice">
          <legend>{config.choice.question}{config.choice.required === false ? ' (optional)' : ''}</legend>
          {config.choice.options.map(option => (
            <label key={option}><input type="radio" name={`choice-${config.key || 'form'}`} required={config.choice.required !== false} checked={choice === option} onChange={() => setChoice(option)} /> {option}</label>
          ))}
        </fieldset>
      )}
      {config.checks.map((label, index) => (
        <label key={label} className="ar-vax-check">
          <input type="checkbox" required checked={checked[index] || false} onChange={event => setChecked(current => current.map((value, position) => (position === index ? event.target.checked : value)))} /> {label}
        </label>
      ))}
      {config.check_note && <p className="ar-vax-note">{config.check_note}</p>}
      {'certificate' in fields && (
        <label>{config.file_label || 'Training certificate'}{required('certificate') ? '' : ' (optional)'}
          <input type="file" accept=".pdf,.png,.jpg,.jpeg,application/pdf,image/png,image/jpeg" required={required('certificate')} onChange={event => setCertificate(event.target.files?.[0] || null)} />
          {config.certificate_help && <span className="ar-vax-note">{config.certificate_help} PDF, PNG, or JPG, up to 10 MB.</span>}
        </label>
      )}
      <label>{required('message') ? 'Comment or message' : 'Additional details (optional)'} <textarea required={required('message')} maxLength={2000} value={values.message} onChange={set('message')} /></label>
      {error && <p role="alert" className="ar-vax-error">{error}</p>}
      <button type="submit" className="ar-button accent" disabled={saving}>{saving ? 'Submitting…' : submitLabel}</button>
    </form>
  );
};

export default RegistrationRequestForm;
